<template>
    <v-container fluid class="pa-0 evaluations-view">
        <div class="shared-nav">
            <select-option-students :custom-options="options" @change="option = $event" :value="option" />

            <div v-if="option === 'student'" class="nav-search-group">
                <div class="nav-search">
                    <kimsa-text-field class="search-field" @change="search = $event" :value="search"
                        :placeholder="`${$t('common.search')}`" prepend-inner-icon="mdi-magnify" dense />
                </div>
                <v-menu offset-y left min-width="180">
                    <template v-slot:activator="{ on: onMenu, attrs }">
                        <v-tooltip bottom :color="tooltipBg" content-class="bp-tooltip">
                            <template v-slot:activator="{ on: onTip }">
                                <button class="filter-btn" :class="{ active: !!teamFilter }" v-bind="attrs"
                                    v-on="{ ...onMenu, ...onTip }">
                                    <v-icon size="18">mdi-filter-outline</v-icon>
                                </button>
                            </template>
                            <span>{{ $t('common.filter') }}</span>
                        </v-tooltip>
                    </template>
                    <v-list dense>
                        <v-list-item link color="accent" :input-value="!teamFilter" @click="teamFilter = null">
                            <v-list-item-title>{{ $t('common.all') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item v-for="team in teams" :key="team.id" link color="accent"
                            :input-value="teamFilter === team.id" @click="teamFilter = team.id">
                            <v-list-item-title>{{ team.name }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>

            <div class="nav-actions">
                <v-menu offset-y left min-width="190">
                    <template v-slot:activator="{ on: onMenu, attrs }">
                        <v-tooltip bottom :color="tooltipBg" content-class="bp-tooltip">
                            <template v-slot:activator="{ on: onTip }">
                                <button class="eval-fab" v-bind="attrs" v-on="{ ...onMenu, ...onTip }">
                                    <v-icon size="18" color="#0C0E12">mdi-plus</v-icon>
                                </button>
                            </template>
                            <span>{{ $t('common.actions') }}</span>
                        </v-tooltip>
                    </template>
                    <v-list dense>
                        <v-list-item link @click="showCreateTemplate">
                            <v-icon size="15" :color="menuIconColor" class="mr-2">mdi-file-plus-outline</v-icon>
                            <v-list-item-title>{{ $t('tooltips.create_template') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item link @click="showEditTemplate">
                            <v-icon size="15" :color="menuIconColor" class="mr-2">mdi-file-document-edit-outline</v-icon>
                            <v-list-item-title>{{ $t('tooltips.edit_template') }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>
        </div>

        <div v-show="option === 'student'" class="table-card">
            <kimsa-grid-table :headers="headers" :items="filteredStudents" :loading="loading" item-key="id"
                :per-page="10" :page="page" flat :no-data-text="$t('common.not_found')">
                <template v-slot:item.name="{ item }">
                    <div class="student-cell">
                        <student-avatar :avatar="item.avatar" :name="item.name" :avatar-key="item.id" :size="34"
                            :radius="8" />
                        <div class="student-cell-text">
                            <truncated-text :text="item.name" />
                            <div v-if="teamName(item)" class="student-team">{{ teamName(item) }}</div>
                        </div>
                    </div>
                </template>
                <template v-slot:item.lastEvaluation="{ item }">
                    <span class="ev-date" :class="{ 'ev-date--empty': !item.lastEvaluation }">
                        {{ item.lastEvaluation || $t('common.without_data') }}
                    </span>
                </template>
                <template v-slot:item.recordsCount="{ item }">
                    <span class="ev-count" :class="{ 'ev-count--empty': !item.recordsCount }">
                        <v-icon v-if="item.recordsCount" size="12">mdi-checkbox-marked-circle-outline</v-icon>
                        {{ item.recordsCount }} {{ $tc('common.records', item.recordsCount) }}
                    </span>
                </template>
                <template v-slot:item.actions="{ item }">
                    <div class="row-actions">
                        <template v-if="item.assignedEvaluationId">
                            <v-tooltip top :color="tooltipBg" content-class="bp-tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <button class="icon-btn add-btn" v-bind="attrs" v-on="on"
                                        @click="loadDataEvaluation(item)">
                                        <v-icon size="15">mdi-plus</v-icon>
                                    </button>
                                </template>
                                <span>{{ $t('tooltips.load_data') }}</span>
                            </v-tooltip>
                            <v-tooltip top :color="tooltipBg" content-class="bp-tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <button class="icon-btn" v-bind="attrs" v-on="on"
                                        @click="goToEvaluationStatistics(item)">
                                        <v-icon size="15">mdi-eye-outline</v-icon>
                                    </button>
                                </template>
                                <span>{{ $t('tooltips.see_data') }}</span>
                            </v-tooltip>
                            <v-tooltip top :color="tooltipBg" content-class="bp-tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <button class="icon-btn" v-bind="attrs" v-on="on" @click="showCreateReport(item)">
                                        <v-icon size="15">mdi-file-document-edit-outline</v-icon>
                                    </button>
                                </template>
                                <span>{{ $t('tooltips.create_report') }}</span>
                            </v-tooltip>
                            <v-tooltip top :color="tooltipBg" content-class="bp-tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <button class="icon-btn" v-bind="attrs" v-on="on" @click="showHitoryReports(item)">
                                        <v-icon size="15">mdi-file-document-refresh-outline</v-icon>
                                    </button>
                                </template>
                                <span>{{ $t('tooltips.see_report_history') }}</span>
                            </v-tooltip>
                            <v-tooltip top :color="tooltipBg" content-class="bp-tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <button class="icon-btn danger-btn" v-bind="attrs" v-on="on"
                                        @click="showDeleteEvaluation(item)">
                                        <v-icon size="15">$delete</v-icon>
                                    </button>
                                </template>
                                <span>{{ $t('tooltips.delete_evaluation') }}</span>
                            </v-tooltip>
                        </template>
                        <v-tooltip v-else top :color="tooltipBg" content-class="bp-tooltip">
                            <template v-slot:activator="{ on, attrs }">
                                <button class="icon-btn add-btn" v-bind="attrs" v-on="on" @click="assignTemplate(item)">
                                    <v-icon size="15">mdi-plus</v-icon>
                                </button>
                            </template>
                            <span>{{ $t('tooltips.add_evaluation') }}</span>
                        </v-tooltip>
                    </div>
                </template>
                <template v-slot:footer>
                    <div class="pagination-bar">
                        <span class="pagination-info">
                            {{ $tc('common.students_count', filteredStudents.length, { n: filteredStudents.length }) }}
                        </span>
                        <kimsa-pagination v-if="pageCount > 1" :value="page" @page="page = $event"
                            :pageCount="pageCount" :total-visible="isMobile ? 4 : 7" height="auto" no-right />
                    </div>
                </template>
            </kimsa-grid-table>
        </div>

        <team-evaluation-table v-show="option === 'team'" :students="students"
            @click:create:group="showCreateGroup" @click:load:evaluation="loadDataEvaluation"
            @click:evaluation:statistics="goToEvaluationStatistics" @click:create:report="showCreateReport"
            @click:show:report="showHitoryReports" @click:assign="assignTemplate"
            @click:evaluation:delete="showDeleteEvaluation" @click:student:delete="showDeleteStudent" />

        <assign-evaluation-dialog :type="option" :coach-id="coachId" @assigned="onAssignedEvaluation" ref="assign" />
        <load-data-evaluation-dialog :coach-id="coachId" ref="loadData" />
        <load-data-evaluation-group-dialog :coach-id="coachId" :students="students" ref="loadDataGroup" />
        <create-report-student :coach-id="coachId" ref="report" />
        <history-report-student ref="history" :coach-id="coachId" @showReport="onShowReport" />
        <view-report-student ref="reportView" />
        <create-group-evaluation-dialog ref="createGroups" />
        <create-report-team :coach-id="coachId" ref="reportTeam" />
        <create-evaluation-dialog :coach-id="coachId" ref="template" />
        <evaluations-list-dialog :coach-id="coachId" ref="templateList" />
        <kimsa-confirm :title="$t('common.delete_assigned_evaluation_title')"
            :content="$t('common.delete_assigned_evaluation_body')" @confirm="onConfirmDeleteEvaluation"
            :close-on-confirm="false" :loading="loadingDelete" alert-mode ref="confirm" />
        <kimsa-confirm :title="$t('common.delete_student_team_title')" :content="$t('common.delete_student_team_body')"
            @confirm="onConfirmDeleteStudentTeam" :close-on-confirm="false" :loading="loadingDelete" alert-mode
            ref="confirmStudent" />
    </v-container>
</template>

<script>

import { mapActions, mapState } from "vuex";
import { db } from "@/utils/Firebase";
import { fetchEvaluationSummaries } from "@/utils/Statistics";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaPagination from "@/components/common/KimsaPagination";
import SelectOptionStudents from "@/components/coach/SelectOptionStudents";
import AssignEvaluationDialog from "@/components/coach/AssignEvaluationDialog";
import LoadDataEvaluationDialog from "@/components/coach/LoadDataEvaluationDialog";
import LoadDataEvaluationGroupDialog from "@/components/coach/LoadDataEvaluationGroupDialog";
import CreateReportStudent from '../../components/common/CreateReportStudent.vue';
import HistoryReportStudent from '../../components/common/HistoryReportStudent.vue';
import ViewReportStudent from '../../components/common/ViewReportStudent.vue';
import TeamEvaluationTable from '../../components/student/TeamEvaluationTable.vue';
import CreateGroupEvaluationDialog from '../../components/coach/CreateGroupEvaluationDialog.vue';
import CreateReportTeam from '../../components/common/CreateReportTeam.vue';
import CreateEvaluationDialog from '../../components/coach/CreateEvaluationDialog.vue';
import EvaluationsListDialog from '../../components/coach/EvaluationsListDialog.vue';
import KimsaConfirm from '../../components/common/KimsaConfirm.vue';
import TruncatedText from '../../components/common/TruncatedText.vue';
import StudentAvatar from '../../components/common/StudentAvatar.vue';
import KimsaGridTable from '../../components/common/KimsaGridTable.vue';

export default {
    components: {
        LoadDataEvaluationGroupDialog,
        LoadDataEvaluationDialog,
        AssignEvaluationDialog,
        SelectOptionStudents,
        KimsaPagination,
        KimsaTextField,
        CreateReportStudent,
        HistoryReportStudent,
        ViewReportStudent,
        TeamEvaluationTable,
        CreateGroupEvaluationDialog,
        CreateReportTeam,
        CreateEvaluationDialog,
        EvaluationsListDialog,
        KimsaConfirm,
        TruncatedText,
        StudentAvatar,
        KimsaGridTable,
    },
    data(vm = this) {
        return {
            students: [],
            loading: false,
            option: 'student',
            search: '',
            teamFilter: null,
            page: 1,
            headers: [
                { text: vm.$t('common.student'), value: 'name', width: 'minmax(180px, 1fr)' },
                { text: vm.$t('common.last_evaluation'), value: 'lastEvaluation', sortable: false, width: '150px' },
                { text: vm.$t('common.evaluations'), value: 'recordsCount', sortable: false, width: '150px' },
                { text: '', value: 'actions', sortable: false, align: 'end', width: '200px' },
            ],
            currentItem: null,
            loadingDelete: false,
        }
    },
    computed: {
        ...mapState(['evaluations', 'groups', 'teams', 'libraryCoach']),
        options(vm = this) {
            return [
                { name: `${vm.$t('common.student')}s`, value: 'student', icon: 'mdi-account' },
                { name: `${vm.$t('common.team')}s`, value: 'team', icon: 'mdi-account-group', feature: 'groupEvaluation' },
            ]
        },
        evaluationFeature(vm = this) {
            return vm.option === 'team' ? 'groupEvaluation' : 'individualEvaluation'
        },
        menuIconColor(vm = this) {
            return vm.$vuetify.theme.dark ? 'primary' : 'accent'
        },
        filteredStudents(vm = this) {
            let list = vm.students
            if (vm.teamFilter) list = list.filter(student => student?.evaluationTeam?.id === vm.teamFilter)
            if (vm.search) {
                const query = vm.search.toLowerCase()
                list = list.filter(student => (student.name || '').toLowerCase().includes(query))
            }
            return list
        },
        pageCount(vm = this) {
            return Math.ceil(vm.filteredStudents.length / 10)
        },
        coachId(vm = this) {
            return vm.isCompany ? vm.libraryCoach?.id || null : vm.currentUser?.uid || null
        }
    },
    async mounted() {
        const vm = this
        const tasks = [vm.loadStudents()]
        if (vm._checkNeedReloadBind(vm.evaluations, vm.coachId)) tasks.push(vm.bindEvaluations(vm.coachId))
        await Promise.all(tasks)
    },
    methods: {
        ...mapActions(['bindEvaluations']),
        teamName(item) {
            const vm = this
            const teamId = item?.evaluationTeam?.id
            if (!teamId) return item?.evaluationTeam?.name || null
            return vm.teams.find(team => team.id === teamId)?.name || item?.evaluationTeam?.name || null
        },
        async loadStudents() {
            const vm = this
            vm.loading = true
            vm.students = []

            let studentsRef = await db.collection('users')
                .where('studentSubscription.coachId', '==', vm.coachId)
                .where('studentSubscription.paused', '==', false)
                .orderBy('name')
                .get()

            vm.students = studentsRef.docs.map(student => {
                const data = student.data()
                return {
                    ...data,
                    id: student.id,
                    avatar: data.avatar || null,
                    lastEvaluation: null,
                    recordsCount: 0,
                }
            })

            await vm.loadEvaluationSummaries()
            vm.loading = false
        },
        async loadEvaluationSummaries() {
            const vm = this
            const ids = vm.students.filter(student => student.assignedEvaluationId).map(student => student.assignedEvaluationId)
            if (!ids.length) return

            try {
                const summaries = await fetchEvaluationSummaries({ coachId: vm.coachId, assignedEvaluationIds: ids })
                const byId = {}
                summaries.forEach(summary => { byId[summary.id] = summary })

                vm.students.forEach(student => {
                    const summary = student.assignedEvaluationId ? byId[student.assignedEvaluationId] : null
                    student.recordsCount = summary?.count || 0
                    student.lastEvaluation = summary?.lastDate
                        ? vm.$moment(summary.lastDate, 'DD-MM-YYYY').format('DD/MM/YYYY')
                        : null
                })
            } catch (err) {
                console.log('loadEvaluationSummaries error', err)
            }
        },
        showCreateTemplate() {
            const vm = this
            if (!vm.requirePlan('individualEvaluation')) return
            vm.$refs.template.show()
        },
        showEditTemplate() {
            const vm = this
            if (!vm.requirePlan('individualEvaluation')) return
            vm.$refs.templateList.show()
        },
        assignTemplate(item) {
            const vm = this
            if (!vm.requirePlan(vm.evaluationFeature)) return
            vm.$refs.assign.show(item.id)
        },
        onAssignedEvaluation(studentId, assignedEvaluationId) {
            const vm = this
            let studentIndex = vm.students.findIndex(student => student.id === studentId)
            if (studentIndex >= 0) vm.$set(vm.students[studentIndex], 'assignedEvaluationId', assignedEvaluationId);
        },
        loadDataEvaluation(item) {
            const vm = this
            if (!vm.requirePlan(vm.evaluationFeature)) return
            if (vm.option === 'student') return vm.$refs.loadData.show(item.id, item.assignedEvaluationId)
            return vm.$refs.loadDataGroup.show(item, item.assignedEvaluationId)
        },
        goToEvaluationStatistics(item) {
            const vm = this
            if (!vm.requirePlan('evaluationStatistics')) return
            vm.$router.push({ name: `${vm.accountType}.evaluations.statistics`, params: { type: vm.option, id: item.id } })
        },
        showCreateReport(item, team = null, group = null) {
            const vm = this;
            if (!vm.requirePlan('evaluationReports')) return;
            (team && group) ? vm.$refs.reportTeam.show(item, team, group) : vm.$refs.report.show(item)
        },
        showHitoryReports(item, team = null, group = null) {
            const vm = this;
            if (!vm.requirePlan('evaluationReports')) return
            const isTeam = team && group
            vm.$refs.history.show(item, isTeam)
        },
        showDeleteEvaluation(item) {
            const vm = this;
            vm.currentItem = item
            vm.$refs.confirm.show()
        },
        async onConfirmDeleteEvaluation() {
            const vm = this;
            vm.loadingDelete = true
            await db.collection('assignedEvaluations').doc(vm.currentItem.assignedEvaluationId).delete()
            if (vm.option === 'student') {
                await db.collection('users').doc(vm.currentItem.id).update({ assignedEvaluationId: null })
                const index = vm.students.findIndex(student => student.id === vm.currentItem.id)
                if (index >= 0) {
                    vm.students[index].assignedEvaluationId = null
                    vm.students[index].lastEvaluation = null
                    vm.students[index].recordsCount = 0
                }
            } else {
                await db.collection('teams').doc(vm.currentItem.id).update({ assignedEvaluationId: null })
            }
            vm.loadingDelete = false
            vm.currentItem = null
            vm.$refs.confirm?.hide()
            vm.toast(vm.$t('common.assigned_evaluation_deleted'))
        },
        onShowReport(item) {
            const vm = this;
            vm.$refs.reportView.show(item)
        },
        showCreateGroup(item) {
            const vm = this;
            vm.$refs.createGroups.show(item)
        },
        showDeleteStudent(item, team, group) {
            const vm = this;
            vm.currentItem = {
                id: item.id,
                evaluationTeamId: team.id,
                groupEvaluationId: group.id,
                evaluationGroups: team.evaluationGroups || [],
            }
            vm.$refs.confirmStudent.show()
        },
        async onConfirmDeleteStudentTeam() {
            const vm = this;
            vm.loadingDelete = true
            let evaluationGroups = vm.currentItem.evaluationGroups || []

            let index = evaluationGroups.findIndex(group => group.id === vm.currentItem.groupEvaluationId)
            if (index >= 0) {
                evaluationGroups[index].students = evaluationGroups[index].students.filter(student => student !== vm.currentItem.id)
                await db.collection('teams').doc(vm.currentItem.evaluationTeamId).update({ evaluationGroups })
                await db.collection('users').doc(vm.currentItem.id).update({ evaluationTeam: null })
                vm.students[vm.students.findIndex(student => student.id === vm.currentItem.id)].evaluationTeam = null
            }

            vm.loadingDelete = false
            vm.currentItem = null
            vm.$refs.confirmStudent?.hide()
            vm.toast(vm.$t('common.student_team_deleted'))
        }
    },
    watch: {
        search() {
            this.page = 1
        },
        teamFilter() {
            this.page = 1
        }
    }
}
</script>

<style scoped>
.evaluations-view {
    padding: 0 24px !important;
}

@media (max-width: 600px) {
    .evaluations-view {
        padding: 0 12px !important;
    }
}

.shared-nav {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 16px;
}

.nav-search-group {
    display: flex;
    align-items: center;
    gap: 6px;
}

.nav-search {
    width: 200px;
    max-width: 100%;
}

.nav-actions {
    margin-left: auto;
}

.filter-btn {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline2-base);
    cursor: pointer;
    color: var(--v-txtSecondary-base);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;
    flex-shrink: 0;
}

.filter-btn:hover {
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
}

.filter-btn.active {
    background: rgba(51, 153, 153, 0.12);
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

.eval-fab {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--brand-gradient);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 10px rgba(204, 255, 51, 0.25);
    transition: transform 0.15s, box-shadow 0.15s;
    flex-shrink: 0;
}

.eval-fab:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 20px rgba(204, 255, 51, 0.4);
}

.table-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 0 0 var(--v-outline2-base) inset;
}

>>>.student-cell .truncated-text {
    font-weight: 600;
}

>>> .kgt-row:hover .truncated-text {
    color: var(--v-primary-base);
}

.theme--light >>> .kgt-row:hover .truncated-text {
    color: var(--v-accent-base);
}

.student-cell {
    display: flex;
    align-items: center;
    gap: 10px;
}

.student-cell-text {
    min-width: 0;
}

.student-team {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.ev-date {
    font-size: 13px;
    color: var(--v-txtSecondary-base);
    white-space: nowrap;
}

.ev-date--empty {
    color: var(--v-txtMuted-base);
}

.ev-count {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    background: var(--v-surface2-base);
    border-radius: 6px;
    padding: 2px 8px;
    white-space: nowrap;
}

.ev-count--empty {
    color: var(--v-txtMuted-base);
    background: transparent;
    padding-left: 0;
}

.row-actions {
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: flex-end;
}

.icon-btn {
    width: 30px;
    height: 30px;
    border-radius: 7px;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-txtMuted-base);
    transition: background 0.12s, color 0.12s;
}

.icon-btn:hover {
    background: var(--v-outline2-base);
    color: var(--v-txtPrimary-base);
}

.icon-btn.add-btn {
    color: var(--v-primary-base);
}

.theme--light .icon-btn.add-btn {
    color: var(--v-accent-base);
}

.icon-btn.add-btn:hover {
    background: rgba(204, 255, 51, 0.12);
    color: var(--v-primary-base);
}

.theme--light .icon-btn.add-btn:hover {
    background: rgba(51, 153, 153, 0.12);
    color: var(--v-accent-base);
}

.icon-btn.danger-btn:hover {
    background: rgba(232, 92, 92, 0.12);
    color: var(--v-error-base);
}

.pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 16px;
    border-top: 1px solid var(--v-outline-base);
}

.pagination-info {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
}
</style>
